import { FC, useEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import {
  Box,
  VStack,
  HStack,
  Heading,
  Text,
  Button,
  Badge,
  Input,
  Textarea,
  NativeSelectRoot,
  NativeSelectField,
} from '@chakra-ui/react';
import { FiPlus, FiX } from 'react-icons/fi';
import { format } from 'date-fns';
import { ja } from 'date-fns/locale';
import { useVaccinationStore } from '@/stores/vaccinationStore';
import {
  VaccinationRecord,
  VaccinationFormData,
  VaccinationType,
  VACCINATION_TYPE_LABELS,
  VACCINATION_TYPES,
  RABIES_VALID_DAYS,
} from '@/types/vaccination';
import {
  getDogVaccinationAlert,
  getAlertLabel,
  getAlertColor,
} from '@/utils/vaccinationAlert';
import { toaster } from '@/components/ui/toaster';

interface VaccinationSectionProps {
  dogId: string;
  userId: string;
}

interface VaccinationInputForm {
  type: VaccinationType | '';
  vaccinatedAt: string;
  nextDueDate: string;
  clinicName: string;
  notes: string;
}

const emptyForm: VaccinationInputForm = {
  type: '',
  vaccinatedAt: '',
  nextDueDate: '',
  clinicName: '',
  notes: '',
};

/**
 * 接種記録 1 件の表示
 */
const VaccinationItem: FC<{
  record: VaccinationRecord;
  onEdit: (record: VaccinationRecord) => void;
  onDelete: (record: VaccinationRecord) => void;
  disabled?: boolean;
}> = ({ record, onEdit, onDelete, disabled }) => {
  return (
    <Box p={3} bg="gray.50" borderRadius="md">
      <VStack align="stretch" gap={1}>
        <HStack justify="space-between">
          <Badge colorPalette="blue">{VACCINATION_TYPE_LABELS[record.type]}</Badge>
          <HStack gap={1}>
            <Button size="xs" variant="ghost" onClick={() => onEdit(record)} disabled={disabled}>
              編集
            </Button>
            <Button
              size="xs"
              variant="ghost"
              colorPalette="red"
              onClick={() => onDelete(record)}
              disabled={disabled}
              data-testid="vaccination-delete-button"
            >
              削除
            </Button>
          </HStack>
        </HStack>
        <HStack gap={4} flexWrap="wrap">
          <Text fontSize="sm">
            接種日: {format(record.vaccinatedAt.toDate(), 'yyyy年M月d日', { locale: ja })}
          </Text>
          {record.nextDueDate && (
            <Text fontSize="sm" color="gray.600">
              次回期限:{' '}
              {format(record.nextDueDate.toDate(), 'yyyy年M月d日', { locale: ja })}
            </Text>
          )}
        </HStack>
        {(record.clinicName || record.notes) && (
          <HStack gap={4} flexWrap="wrap">
            {record.clinicName && (
              <Text fontSize="xs" color="gray.500">
                クリニック: {record.clinicName}
              </Text>
            )}
            {record.notes && (
              <Text fontSize="xs" color="gray.500">
                備考: {record.notes}
              </Text>
            )}
          </HStack>
        )}
      </VStack>
    </Box>
  );
};

/**
 * ワクチン接種記録セクション（詳細画面・編集画面で共用）
 *
 * - 期限切れ・期限間近のアラートバナー
 * - 接種記録の一覧
 * - 接種記録の追加・編集・削除（インライン入力）
 */
export const VaccinationSection: FC<VaccinationSectionProps> = ({ dogId, userId }) => {
  const {
    vaccinations,
    loading,
    fetchVaccinations,
    createVaccination,
    updateVaccination,
    deleteVaccination,
  } = useVaccinationStore();

  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);

  const { register, handleSubmit, reset, watch, setValue } =
    useForm<VaccinationInputForm>({
      defaultValues: emptyForm,
    });

  const watchType = watch('type');
  const watchVaccinatedAt = watch('vaccinatedAt');
  // ユーザーが次回期限を手入力したら自動入力を止める
  const nextDueManuallyEdited = useRef(false);

  useEffect(() => {
    if (dogId && userId) {
      fetchVaccinations(dogId, userId);
    }
  }, [dogId, userId, fetchVaccinations]);

  // 狂犬病は接種日を入れた時点で次回期限（接種日+1年）を自動入力する
  useEffect(() => {
    if (
      watchType === 'rabies' &&
      watchVaccinatedAt &&
      !nextDueManuallyEdited.current
    ) {
      const base = new Date(`${watchVaccinatedAt}T00:00:00`);
      if (!Number.isNaN(base.getTime())) {
        base.setDate(base.getDate() + RABIES_VALID_DAYS);
        setValue('nextDueDate', format(base, 'yyyy-MM-dd'));
      }
    }
  }, [watchType, watchVaccinatedAt, setValue]);

  const alert = getDogVaccinationAlert(vaccinations);

  const openCreateForm = () => {
    setEditingId(null);
    nextDueManuallyEdited.current = false;
    reset(emptyForm);
    setIsFormOpen(true);
  };

  const openEditForm = (record: VaccinationRecord) => {
    setEditingId(record.id);
    // 既存の次回期限は尊重し、自動入力で上書きしない
    nextDueManuallyEdited.current = true;
    reset({
      type: record.type,
      vaccinatedAt: record.vaccinatedAt.toDate().toISOString().split('T')[0],
      nextDueDate: record.nextDueDate
        ? record.nextDueDate.toDate().toISOString().split('T')[0]
        : '',
      clinicName: record.clinicName ?? '',
      notes: record.notes ?? '',
    });
    setIsFormOpen(true);
  };

  const closeForm = () => {
    setIsFormOpen(false);
    setEditingId(null);
    nextDueManuallyEdited.current = false;
    reset(emptyForm);
  };

  const onSubmit = async (data: VaccinationInputForm) => {
    if (!data.type || !data.vaccinatedAt) {
      toaster.error({ title: '種類と接種日を入力してください' });
      return;
    }

    const formData: VaccinationFormData = {
      type: data.type,
      vaccinatedAt: new Date(data.vaccinatedAt),
      nextDueDate: data.nextDueDate ? new Date(data.nextDueDate) : undefined,
      clinicName: data.clinicName.trim() || undefined,
      notes: data.notes.trim() || undefined,
    };

    try {
      if (editingId) {
        await updateVaccination(dogId, editingId, formData, userId);
        toaster.success({ title: 'ワクチン接種記録を更新しました' });
      } else {
        await createVaccination(dogId, formData, userId);
        toaster.success({ title: 'ワクチン接種記録を追加しました' });
      }
      closeForm();
    } catch {
      toaster.error({ title: '保存に失敗しました' });
    }
  };

  const handleDelete = async (record: VaccinationRecord) => {
    try {
      await deleteVaccination(dogId, record.id, userId);
      toaster.success({ title: 'ワクチン接種記録を削除しました' });
    } catch {
      toaster.error({ title: '削除に失敗しました' });
    }
  };

  return (
    <Box bg="white" p={6} borderRadius="lg" shadow="md" data-testid="vaccination-section">
      <VStack align="stretch" gap={4}>
        <HStack justify="space-between">
          <Heading size="md">ワクチン接種記録</Heading>
          {!isFormOpen && (
            <Button size="sm" colorPalette="blue" onClick={openCreateForm}>
              <HStack gap={1}>
                <FiPlus />
                <Text>接種記録を追加</Text>
              </HStack>
            </Button>
          )}
        </HStack>

        {/* 期限アラートバナー */}
        {alert.level !== 'none' && alert.record && (
          <Box
            p={3}
            borderRadius="md"
            borderLeft="4px solid"
            borderLeftColor={`${getAlertColor(alert.level)}.500`}
            bg={`${getAlertColor(alert.level)}.50`}
          >
            <HStack gap={2}>
              <Badge colorPalette={getAlertColor(alert.level)}>
                {getAlertLabel(alert.level)}
              </Badge>
              <Text fontSize="sm" color={`${getAlertColor(alert.level)}.700`}>
                {VACCINATION_TYPE_LABELS[alert.record.type]}ワクチンの期限
                {alert.level === 'overdue'
                  ? `が ${Math.abs(alert.daysUntilDue)} 日超過しています`
                  : `まで残り ${alert.daysUntilDue} 日です`}
              </Text>
            </HStack>
          </Box>
        )}

        {/* 入力フォーム */}
        {isFormOpen && (
          <Box p={4} borderWidth={1} borderRadius="md" borderColor="gray.200">
            <form onSubmit={handleSubmit(onSubmit)}>
              <VStack align="stretch" gap={3}>
                <HStack justify="space-between">
                  <Text fontWeight="medium" fontSize="sm">
                    {editingId ? '接種記録を編集' : '接種記録を追加'}
                  </Text>
                  <Button size="xs" variant="ghost" onClick={closeForm}>
                    <FiX />
                  </Button>
                </HStack>

                <Box>
                  <Text fontSize="sm" fontWeight="medium" mb={1}>
                    種類 <Text as="span" color="red.500">*</Text>
                  </Text>
                  <NativeSelectRoot size="sm">
                    <NativeSelectField {...register('type')}>
                      <option value="">選択してください</option>
                      {VACCINATION_TYPES.map((type) => (
                        <option key={type} value={type}>
                          {VACCINATION_TYPE_LABELS[type]}
                        </option>
                      ))}
                    </NativeSelectField>
                  </NativeSelectRoot>
                </Box>

                <HStack gap={4} align="start">
                  <Box flex={1}>
                    <Text fontSize="sm" fontWeight="medium" mb={1}>
                      接種日 <Text as="span" color="red.500">*</Text>
                    </Text>
                    <Input type="date" size="sm" {...register('vaccinatedAt')} />
                  </Box>
                  <Box flex={1}>
                    <Text fontSize="sm" fontWeight="medium" mb={1}>
                      次回期限
                    </Text>
                    <Input
                      type="date"
                      size="sm"
                      {...register('nextDueDate', {
                        onChange: () => {
                          nextDueManuallyEdited.current = true;
                        },
                      })}
                    />
                    {watchType === 'rabies' && (
                      <Text fontSize="xs" color="gray.500" mt={1}>
                        接種日から1年後を自動入力します（手動で変更可）
                      </Text>
                    )}
                  </Box>
                </HStack>

                <Box>
                  <Text fontSize="sm" fontWeight="medium" mb={1}>
                    クリニック名
                  </Text>
                  <Input size="sm" placeholder="任意" {...register('clinicName')} />
                </Box>

                <Box>
                  <Text fontSize="sm" fontWeight="medium" mb={1}>
                    備考
                  </Text>
                  <Textarea rows={2} placeholder="任意" {...register('notes')} />
                </Box>

                <HStack justify="flex-end" gap={2}>
                  <Button size="sm" variant="outline" onClick={closeForm} type="button">
                    キャンセル
                  </Button>
                  <Button size="sm" colorPalette="blue" type="submit" loading={loading}>
                    {editingId ? '更新' : '追加'}
                  </Button>
                </HStack>
              </VStack>
            </form>
          </Box>
        )}

        {/* 記録一覧 */}
        {vaccinations.length === 0 && !loading ? (
          <Text fontSize="sm" color="gray.500">
            接種記録がありません
          </Text>
        ) : (
          <VStack align="stretch" gap={2}>
            {vaccinations.map((record) => (
              <VaccinationItem
                key={record.id}
                record={record}
                onEdit={openEditForm}
                onDelete={handleDelete}
                disabled={loading}
              />
            ))}
          </VStack>
        )}
      </VStack>
    </Box>
  );
};

export default VaccinationSection;
